<template>
    <div class="row">
        <div class="col-12" v-if="zona">
            <TerrenoJuegoComponent :direccion_campo="zona.direccion_campo"/>

            <div class="container-butacas-wrapper" v-if="!loading && zona.with_seats">
                <div :class="'container-butacas text-align-' + zona.print_align">
                    <template v-for="(butacas, fila) in filas">
                        <span :key="fila">
                            <span class="fila">{{ fila }}</span>
                            <template v-for="(asiento, index) in butacas">
                                <AsientoComponent
                                    :asiento="asiento"
                                    :fila="fila"
                                    :key="index+'_2'"
                                    :allow-blocked="true"
                                    :bloqueada-pointer-cursor="true"
                                    @toggle="() => toggleSeleccion(fila, asiento, true, true)"
                                />
                            </template>

                            <br />
                        </span>
                    </template>
                </div>
            </div>
            <div v-if="!zona.with_seats">
                <FormInput label="Nº de butacas a bloquear" type="number" v-model="n_butacas" />
            </div>
            <div class="col-12 text-center" style="height: 150px" v-if="loading">
                <LoaderComponent/>
            </div>
            <!-- Estado de error recuperable: sin esto, un fallo de carga dejaba
                 el loader activo indefinidamente y sin salida. -->
            <div class="col-12 text-center" v-else-if="zonaError">
                <p class="text-danger">{{ zonaError }}</p>
                <Button class="m-2" @click="getZona(zona.id)">Reintentar</Button>
            </div>
        </div>
        <leyenda-color />
        <div class="col-12"><hr /></div>
        <div class="col-12">
            <Button class="m-3" @click="selectAll">Seleccionar todas</Button>
            <Button class="m-3" @click="clearAll">Limpiar todas</Button>
            <Button class="m-3" @click="showModalBloquearButacas" :disabled="bloquear_boton || bloqueando">Bloquear / Liberar</Button>
        </div>

        <div class="col-12" v-if="zona && !loading">
            <div class="form-group">
                <label>AFORO REGIÓN: {{ zona.aforo ? zona.aforo : zona.total_asientos }}</label>
            </div>
            <div class="form-group">
                <label>AFORO A LA VENTA: {{ total_en_venta }}</label>
            </div>
            <div class="form-group">
                <label>AFORO BLOQUEADO: {{ zona.bloqueados }}</label>
            </div>
        </div>

        <div class="col-12" v-if="zona && !loading">
            <h4>LISTADO DE BLOQUEOS</h4>
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                <table class="table table-sm table-striped">
                    <thead>
                        <tr>
                            <th>Fila</th>
                            <th>Asiento/Nº</th>
                            <th>Etiqueta</th>
                            <th>Concepto</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="bloqueoLista.length === 0">
                            <td colspan="4" class="text-center text-muted">
                                No hay butacas bloqueadas en esta región.
                            </td>
                        </tr>
                        <tr v-for="(item, key) in bloqueoLista" :key="key">
                            <td>{{ item.fila }}</td>
                            <td>{{ item.asiento }}</td>
                            <td class="tw:flex tw:items-center tw:gap-2">
                                <span
                                    class="butaca"
                                    :style="{ backgroundColor: item.etiqueta_color }"
                                ></span>
                                {{ item.etiqueta_nombre }}
                            </td>
                            <td>{{ item.concepto }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="col-12 text-center" v-if="bloqueando" style="height: 150px">
            <LoaderComponent/>
        </div>

        <BloquearButacas
            :butacas="asientos"
            @bloquearButacas="onConfirmarBloqueo"
            @close="closeModalBloquearButacas"
        />
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, getCurrentInstance } from 'vue'
    import store from '@/store'
    import AsientoComponent from "@/components/backend/stepper/components/AsientoComponent";
    import TerrenoJuegoComponent from '@/components/common/Asientos/TerrenoJuegoComponent.vue';
    import LoaderComponent from '@/components/preloader/LoaderComponent';
    import LeyendaColor from './LeyendaColor.vue';
    import BloquearButacas from '@/components/backend/stepper/components/BloquearButacas.vue';
    import { useBlockSeats } from '@/composables/admin/abonos/useBlockSeats'
    import { useZonaSeats } from '@/composables/admin/abonos/useZonaSeats'
    import { buildRegionBlockerPayload } from '@/composables/admin/abonos/blockSeatsPayload'
    import { useModal } from '@/composables/common/useModal'

    const props = defineProps({
        zona_seleccionada: {
            type: Object,
            required: true
        }
    })

    const instance = getCurrentInstance()
    const config = instance?.proxy?.config ?? store.getters.getConfig
    const modal = useModal()

    const filas = ref(null)
    const asientos = ref([])
    const n_butacas = ref(0)
    const zona = ref(null)

    // `loading` y su error vienen del composable de carga de zona, que es quien
    // realiza la petición.
    const { loading, error: zonaError, loadZona } = useZonaSeats({
        onLoaded: data => {
            filas.value = data.configuracion_asientos ?? {}

            if (zona.value) {
                zona.value.total_asientos = data.total_asientos
                zona.value.bloqueados = data.bloqueados
                zona.value.ocupados = data.ocupados
            }
        }
    })

    const {
        bloqueando,
        bloquearAsientos
    } = useBlockSeats({
        endpoint: 'back.abonos.bloquear.asientos',
        payloadBuilder: (asientos, ctx) => buildRegionBlockerPayload(asientos, ctx, store.getters.getListaId)
    })

    const total_en_venta = computed(() => {
        // Null-safe: la zona se asigna en onMounted, pero el primer render puede
        // ejecutar este computed antes con zona.value === null (loading parte en
        // false). Evitar crash en render.
        if (!zona.value) return 0
        const aforo = zona.value.aforo ? zona.value.aforo : zona.value.total_asientos
        return aforo - zona.value.bloqueados - zona.value.ocupados
    })

    const bloqueoLista = computed(() => {
        const lista = []
        if (filas.value) {
            for (const fila in filas.value) {
                for (const asiento of filas.value[fila]) {
                    if (asiento.ocupado === 3) {
                        lista.push({
                            fila,
                            asiento: asiento.asiento,
                            etiqueta_nombre: asiento.info?.etiqueta_bloqueo?.name || '',
                            etiqueta_color: asiento.info?.etiqueta_bloqueo?.color || '',
                            concepto: asiento.info?.concepto || ''
                        })
                    }
                }
            }
        }
        return lista
    })

    const bloquear_boton = computed(() => {
        return asientos.value.length === 0 && n_butacas.value === 0
    })

    function getZona(id) {
        return loadZona(id)
    }

    /**
     * Sólo son seleccionables las butacas libres (0) o bloqueadas (3 sin
     * reserva). Aceptar `ocupado != 1` dejaba entrar pendientes y reservadas,
     * que el endpoint no debería recibir; `selectAll()` ya filtraba 0 y 3, así
     * que ambos caminos comparten ahora el mismo criterio.
     */
    function isSeleccionable(asiento) {
        if (!asiento) return false;

        return (asiento.ocupado === 0 || asiento.ocupado === 3) && !asiento.info?.reserva_id
    }

    function toggleSeleccion(fila, asiento) {
        if (isSeleccionable(asiento)) {
            for (var i in filas.value[fila]) {
                if (filas.value[fila][i].asiento == asiento.asiento) {
                    if (filas.value[fila][i].seleccionado) {
                        for (var j in asientos.value) {
                            if (asientos.value[j].row == fila && asientos.value[j].seat == asiento.asiento) {
                                asientos.value.splice(j, 1)
                            }
                        }
                    } else {
                        const data = {
                            row: fila,
                            seat: filas.value[fila][i].asiento,
                            region_id: zona.value.id,
                            zona_nombre: zona.value.name,
                            bloqueada: filas.value[fila][i].ocupado === 3
                        }
                        asientos.value.push(data)
                    }
                    filas.value[fila][i].seleccionado = !filas.value[fila][i].seleccionado
                }
            }
        }
    }

    function selectAll() {
        if (zona.value.with_seats) {
            for (var fila in filas.value) {
                for (var asiento in filas.value[fila]) {
                    const candidato = filas.value[fila][asiento]
                    // Mismo criterio que toggleSeleccion: libres (0) o bloqueadas
                    // (3 sin reserva), sin seleccionar todavía.
                    if (isSeleccionable(candidato) && !candidato.seleccionado) {
                        const data = {
                            row: fila,
                            seat: candidato.asiento,
                            region_id: zona.value.id,
                            zona_nombre: zona.value.name,
                            bloqueada: candidato.ocupado === 3
                        }
                        asientos.value.push(data)

                        candidato.seleccionado = !candidato.seleccionado
                    }
                }
            }
        } else {
            n_butacas.value = zona.value.aforo
        }
    }

    function clearAll() {
        if (zona.value.with_seats) {
            asientos.value = []
            for (var fila in filas.value) {
                for (var asiento in filas.value[fila]) {
                    if (filas.value[fila][asiento].seleccionado) {
                        filas.value[fila][asiento].seleccionado = !filas.value[fila][asiento].seleccionado
                    }
                }
            }
        } else {
            n_butacas.value = zona.value.aforo
        }
    }

    function showModalBloquearButacas() {
        modal.show('bloquearButacas')
    }

    function closeModalBloquearButacas() {
        modal.hide('bloquearButacas')
    }

    function onConfirmarBloqueo(conceptoModal, etiquetaModal) {
        closeModalBloquearButacas()
        bloquearAsientos(asientos, {
            concepto: conceptoModal,
            etiqueta: etiquetaModal,
            n_butacas: n_butacas.value,
            zona_id: zona.value.id
        }, () => {
            asientos.value = []
            n_butacas.value = 0
            getZona(zona.value.id)
        })
    }

    onMounted(() => {
        zona.value = Object.assign({}, props.zona_seleccionada)
        getZona(zona.value.id)
    })

    // Exponer la API pública para tests de <script setup> (VTU no accede a los
    // refs internos).
    defineExpose({
        asientos,
        n_butacas,
        zona,
        onConfirmarBloqueo,
        getZona,
        toggleSeleccion
    })
</script>
